<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CHANNEL_THEMES, LOGO, CHECK } from './channelThemes';

const props = defineProps({
  channel: {
    type: String,
    required: true,
    validator: value => Object.hasOwn(CHANNEL_THEMES, value),
  },
  preview: { type: Object, default: null },
  sender: { type: String, default: '' },
  time: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  missingMessage: { type: String, required: true },
});
const { t } = useI18n({
  useScope: 'local',
  messages: {
    ru: {
      label: 'Мобильный предпросмотр {channel}',
      sender: 'Клиника',
      sample: 'Предпросмотр',
      loading: 'Загрузка…',
      calendar: 'Добавить в календарь',
      reminder: 'Напоминание за {minutes} мин',
      image: 'Изображение сообщения',
      imageError: 'Не удалось показать изображение',
      sampleDelivery: 'Пример отметки сообщения, не статус реальной доставки',
    },
    en: {
      label: '{channel} mobile preview',
      sender: 'Clinic',
      sample: 'Preview',
      loading: 'Loading…',
      calendar: 'Add to calendar',
      reminder: 'Remind {minutes} min before',
      image: 'Message image',
      imageError: 'Unable to display image',
      sampleDelivery: 'Sample message indicator, not a real delivery status',
    },
  },
});
const theme = computed(() => CHANNEL_THEMES[props.channel]);
const imageFailed = ref(false);
const imageUrl = computed(() => {
  const value = props.preview?.image_url;
  if (!value) return '';
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? value : '';
  } catch {
    return '';
  }
});
watch([() => props.channel, imageUrl], () => {
  imageFailed.value = false;
});
const configured = computed(
  () => props.preview && !props.preview.error && !props.loading && !props.error
);
</script>

<template>
  <figure
    data-testid="mobile-preview"
    :data-channel="channel"
    :aria-label="t('label', { channel: theme.label })"
    :aria-busy="loading"
    class="w-full max-w-[320px] mx-auto mt-4"
  >
    <div class="p-2 shadow-lg rounded-[2.4rem] bg-[#0f1115]">
      <div class="overflow-hidden rounded-[1.9rem] bg-white">
        <div :class="theme.bar">
          <div class="flex justify-center pt-2">
            <div class="w-20 rounded-full h-1.5 bg-black/25" />
          </div>
          <div class="flex items-center gap-2 px-3 py-2 mt-1">
            <span
              class="inline-flex items-center justify-center shrink-0 size-[30px]"
              aria-hidden="true"
              v-html="LOGO[channel]"
            />
            <div class="min-w-0 leading-tight">
              <div class="text-[13px] font-semibold text-white truncate">
                {{ sender || t('sender') }}
              </div>
              <div class="text-[10px] text-white/70">
                {{ theme.label }} · {{ t('sample') }}
              </div>
            </div>
          </div>
        </div>
        <div
          data-testid="mobile-chat-screen"
          class="flex flex-col min-h-[300px] max-h-[520px] overflow-y-auto px-3 py-4"
          :class="theme.wall"
        >
          <div
            class="max-w-[84%] min-w-0 overflow-hidden shadow-sm rounded-2xl rounded-tl-sm shrink-0"
            :class="theme.bubble"
          >
            <template v-if="configured">
              <img
                v-if="theme.rich && imageUrl && !imageFailed"
                :key="imageUrl"
                :src="imageUrl"
                :alt="t('image')"
                referrerpolicy="no-referrer"
                class="object-cover w-full max-h-40"
                @error="imageFailed = true"
              />
              <p
                v-if="
                  theme.rich && preview.image_url && (!imageUrl || imageFailed)
                "
                class="p-3 text-xs text-n-ruby-11"
              >
                {{ t('imageError') }}
              </p>
              <div class="px-3 py-2">
                <div
                  data-testid="variant-preview"
                  class="text-[13px] leading-snug whitespace-pre-wrap break-words"
                >
                  {{ preview.text || '—' }}
                </div>
                <div
                  class="flex items-center justify-end gap-1 mt-1 text-[10px] opacity-50"
                >
                  <span>{{ time }}</span>
                  <span
                    v-if="theme.checks"
                    :title="t('sampleDelivery')"
                    v-html="CHECK"
                  />
                </div>
              </div>
            </template>
            <p
              v-else
              class="px-3 py-2 text-[13px] whitespace-pre-wrap break-words"
              :class="{ 'text-n-ruby-11': error }"
            >
              {{ loading ? t('loading') : error || missingMessage }}
            </p>
          </div>
          <template v-if="configured && theme.rich">
            <div
              v-if="preview.ical"
              data-testid="mobile-calendar"
              class="mt-1.5 max-w-[84%] flex items-center gap-2 px-3 py-2 shadow-sm rounded-xl bg-white text-[#111b21] shrink-0"
            >
              <span
                class="i-lucide-calendar-days shrink-0 size-5"
                :class="theme.accent"
                aria-hidden="true"
              />
              <div class="min-w-0 leading-tight">
                <div class="text-[12px] font-medium break-words">
                  {{ preview.ical.title }}
                </div>
                <div class="text-[10px] opacity-60">{{ t('calendar') }}</div>
                <div class="text-[10px] opacity-60">
                  {{
                    t('reminder', { minutes: preview.ical.alarm_minutes || 0 })
                  }}
                </div>
              </div>
            </div>
            <div
              v-if="preview.buttons?.length"
              data-testid="mobile-buttons"
              class="mt-1.5 max-w-[84%] space-y-1 shrink-0"
            >
              <div
                v-for="(button, index) in preview.buttons"
                :key="index"
                :title="button.url || button.action || ''"
                class="px-3 py-1.5 text-[12px] font-medium text-center shadow-sm rounded-lg bg-white break-words"
                :class="theme.accent"
              >
                {{ button.text }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </figure>
</template>
